import { IChatTranscriptMessage } from 'api/concierge';
import { additionalDataBlock } from '../hooks/additionalData';
import { IDialogItem, IMessagePart } from '../types';
import { parseMessage } from './index';

// Sign-in prompts belonged to the live stream; replayed, their resend would re-ask an empty question.
const LIVE_ONLY_BLOCKS: string[] = ['auth-required', 'proceed-button'];

export interface RestoredDialog {
    dialog: IDialogItem[];
    /** Response item uid → the stored message uid, which the report-why request is keyed by. */
    whyMessageUids: Record<string, string>;
}

/**
 * A stored transcript as panel dialog items: each user turn is a request and each assistant turn a
 * response parsed the way a streamed answer is, so a reopened chat renders like the live one.
 */
export function transcriptToDialog(conversationTs: string, messages: IChatTranscriptMessage[]): RestoredDialog {
    const dialog: IDialogItem[] = [];
    const whyMessageUids: Record<string, string> = {};

    messages.forEach((message, index) => {
        const itemUid = `history_${index}_${message.message_uid}`;
        if ('user' === message.role) {
            dialog.push({
                uid: itemUid,
                type: 'request',
                parts: [{ type: 'html', message: message.content, source: 'frontend' }],
            });
            return;
        }

        const parts: IMessagePart[] = parseMessage(message.content ?? '', conversationTs, message.message_uid)
            .map((part) => ({
                ...part,
                jsonBlocks: part.jsonBlocks?.filter((block) => !LIVE_ONLY_BLOCKS.includes(String(block.data_type))),
            }))
            .filter((part) => 'json' !== part.type || (part.jsonBlocks?.length ?? 0) > 0);
        if (message.source_reports) {
            parts.push(...parseMessage(message.source_reports, conversationTs, message.message_uid));
        }
        const additional = message.additional_data ? additionalDataBlock(message.additional_data) : null;
        if (additional) {
            parts.push({ type: 'json', message: '', source: 'frontend', jsonBlocks: [additional] });
        }
        // Clarifications asked during the turn read as they did live: the question, then the reply.
        (message.clarifications ?? []).forEach((clarification, position) => {
            dialog.push({
                uid: `${itemUid}_clarification_${position}_question`,
                type: 'response',
                parts: [{ type: 'markdown', message: clarification.question, source: 'backend' }],
                isClarificationQuestion: true,
            });
            dialog.push({
                uid: `${itemUid}_clarification_${position}_answer`,
                type: 'request',
                parts: [{ type: 'html', message: clarification.answer ?? 'Skip', source: 'frontend' }],
                isClarificationAnswer: true,
            });
        });
        // A turn that never got an answer has nothing to show.
        if (0 === parts.length) {
            return;
        }
        dialog.push({ uid: itemUid, type: 'response', parts, isRestored: true });
        whyMessageUids[itemUid] = message.message_uid;
    });

    return { dialog, whyMessageUids };
}
